import {EventEmitter, Injectable} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {ViewTypeEnum} from '../../core/enums/view-type.enum';
import {UserViewModel} from '../../features/user/settings/user.view-model';

export type ActionsArgs = Record<string, any>;

/** Contextual footer action used by the feature-flagged mobile shell. */
export interface FooterActionContext {
  id: string;
  labelKey: string;
  icon: string;
  visible?: boolean;
  isAllowed?: () => boolean;
  execute: () => void | Promise<void>;
}
@Injectable({
  providedIn: 'root'
})
export class ActionsService {
	get hasAddAction(): boolean {
		return this._addActionCount > 0;
	}
  get permissions(): string[] {
    return this._permissions;
  }

  set permissions(value: string[]) {
    this._permissions = value;
  }
  get viewSubject(): EventEmitter<any> {
    return this._viewSubject;
  }

  get addSubject(): EventEmitter<any> {
    return this._addSubject;
  }

  public viewChangeEnabled: boolean = false;
  public viewType: ViewTypeEnum = ViewTypeEnum.GRID;
  public addDisabled: boolean = false;
  public contextUser: UserViewModel;
  private _permissions: string[];
  private subscriptions: Subscription[] = [];
  private readonly addActionSubscriptions = new Set<Subscription>();
  private _addSubject: EventEmitter<any> = new EventEmitter<any>();
  private _viewSubject: EventEmitter<any> = new EventEmitter<ViewTypeEnum>();
  private _addActionCount = 0;
  private readonly contextualActionSubject = new BehaviorSubject<FooterActionContext | null>(null);
  private readonly contextualActions: Array<{token: number; context: FooterActionContext}> = [];
  private nextContextualActionToken = 0;
  private contextualActionExecutionPending = false;

  public readonly contextualAction$ = this.contextualActionSubject.asObservable();

  public get contextualAction(): FooterActionContext | null {
    const context = this.contextualActions.at(-1)?.context || null;
    return context && context.visible !== false && (context.isAllowed?.() ?? true) ? context : null;
  }

  public get contextualActionCount(): number {
    return this.contextualActions.length;
  }

  constructor() {
  }

  onAdd(args: ActionsArgs) {
    this.addSubject.emit(args);
  }

  onViewChange(viewType: ViewTypeEnum) {
    this.viewType = viewType;
    this.viewSubject.emit(viewType);
  }

  public registerContextualAction(context: FooterActionContext): () => void {
    const token = ++this.nextContextualActionToken;
    this.contextualActions.push({token, context});
    this.publishContextualAction();
    return () => this.removeContextualAction(token);
  }

  public async executeContextualAction(): Promise<boolean> {
    const context = this.contextualAction;
    if (!context || this.contextualActionExecutionPending) {
      return false;
    }
    this.contextualActionExecutionPending = true;
    try {
      await context.execute();
      return true;
    } finally {
      this.contextualActionExecutionPending = false;
    }
  }

  public unsubscribeActions(): void {
    this.viewChangeEnabled = false;
    this.viewType = ViewTypeEnum.GRID;
    this.contextUser = undefined;
    this.addDisabled = false;
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    this.addActionSubscriptions.clear();
    this._addActionCount = 0;
  }

  public unsubscribeAction(subscription: Subscription): void {
    subscription.unsubscribe();
    this.subscriptions = this.subscriptions.filter((item) => item !== subscription);
    if (this.addActionSubscriptions.delete(subscription)) {
      this._addActionCount = Math.max(0, this._addActionCount - 1);
    }
  }

  public subscribeToAddAction(callable: CallableFunction): Subscription {
    const subscription = this.addSubject.subscribe((args: ActionsArgs) => {
      callable(args);
    });

    this._addActionCount++;
    this.addActionSubscriptions.add(subscription);
    this.subscriptions.push(subscription);
    return subscription;
  }

  public subscribeToViewChangeAction(callable: CallableFunction): Subscription {
    this.viewChangeEnabled = true;
    const subscription = this.viewSubject.subscribe((viewType: ViewTypeEnum) => {
      callable(viewType);
    });

    this.subscriptions.push(subscription);
    return subscription;
  }

  private removeContextualAction(token: number): void {
    const index = this.contextualActions.findIndex((registration) => registration.token === token);
    if (index < 0) {
      return;
    }
    this.contextualActions.splice(index, 1);
    this.publishContextualAction();
  }

  private publishContextualAction(): void {
    this.contextualActionSubject.next(this.contextualAction);
  }
}
